<template>
    <div 
        v-if="configItems.length > 0" 
        class="featured-popular-loans"
        :class="{ 'bg-cream': isBgCream }"
    >
        <Section width="lg">
            <div 
                v-if="$validateText(text)"
                class="rich-text text" 
                v-html="$prismic.asHtml(text)"    
            />

            <div class="loan-category-container">
                <div v-if="configItems.length > 1" class="tabs-wrapper">
                    <button 
                        v-for="config in configItems" 
                        :key="config.type"
                        class="category-tab"
                        :class="{ 'is-active': activeTab === config.type }"
                        @click="activeTab = config.type"
                    >
                        {{ config.title }}
                    </button>
                </div>
                <FeaturedItem 
                    v-for="(config, index) in configItems"
                    v-show="activeTab === config.type"
                    :key="`${index}-${config.type}`"
                    :config="config"
                />
            </div>
        </Section>
    </div>
</template>

<script>
import FeaturedItem from './FeaturedItem.vue';
export default {
    components: {
        FeaturedItem
    },
    props: {
        data: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            activeTab: ''
        };
    },
    computed: {
        isBgCream() {
            return this.data.primary.background_color !== 'none';
        },
        text() {
            return this.data.primary.text;
        },
        selectedCategoryTabs() {
            return this.data.items
                .filter(item => item.category_tab)
                .map(item => item.category_tab);
        },
        baseConfigs() {
            return {
                fastLoan: {
                    type: 'fastLoan',
                    title: this.$translate('fast_loan', 'Snabblån'),
                    lenderSource: {
                        listKey: 'fast_loan_lenders',
                        modelKey: 'Loan'
                    },
                    generalLink: {
                        linkKey: 'fast_loan_link',
                        textKey: 'fast_loan_link_text'
                    }
                },

                privateLoan: {
                    type: 'privateLoan',
                    title: this.$translate('private_loan', 'Privatlån'),
                    lenderSource: {
                        listKey: 'private_loan_lenders',
                        modelKey: 'Loan'
                    },
                    generalLink: {
                        linkKey: 'private_loan_link',
                        textKey: 'private_loan_link_text'
                    }
                },

                corporateLoan: {
                    type: 'corporateLoan',
                    title: this.$translate('company_loan', 'Företagslån'),
                    lenderSource: {
                        storeKey: 'lendersCorporate',
                        modelKey: 'CorporateLoan'
                    },
                    generalLink: {
                        linkKey: 'corporate_loan_link',
                        textKey: 'corporate_loan_link_text'
                    }
                },

                mortgageLoan: {
                    type: 'mortgageLoan',
                    title: this.$translate('mortgage_loan', 'Bolån'),
                    lenderSource: {
                        storeKey: 'lendersMortgage',
                        modelKey: 'Mortgage'
                    },
                    generalLink: {
                        linkKey: 'mortgage_loan_link',
                        textKey: 'mortgage_loan_link_text'
                    }
                }
            };
        },
        configItems() {
            const resolved = Object.values(this.baseConfigs).map(config => {

                const lenders = this.getLendersWithModel(
                    config.lenderSource
                );

                return {
                    ...config,
                    lenders: this.getDataToShow(
                        lenders,
                        config.type
                    ),
                    generalLink: this.getGeneralLink(
                        config.generalLink.linkKey,
                        config.generalLink.textKey
                    )
                };
            });

            if (this.selectedCategoryTabs.length > 0) {

                const resolvedByType = resolved.reduce((acc, config) => {
                    acc[config.type] = config;
                    return acc;
                }, {});

                return this.selectedCategoryTabs
                    .map(type => resolvedByType[type])
                    .filter(Boolean);
            }

            return resolved;
        },
    },
    watch: {
        configItems: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.length > 0 && !this.activeTab) {
                    this.activeTab = newVal[0].type;
                }
            }
        }
    },
    methods: {
        getLendersWithModel({ listKey, storeKey, modelKey }) {
            let lenders = [];

            if (listKey) {
                const listLenders = this.$store.state.lists[this.data.primary[listKey]?.id]?.data.lenders;
                lenders = listLenders 
                    ? listLenders
                        .map(item => this.$store.state.lenders[item.lender.id])
                    : [];
            }
            else {
                lenders = Object.values(this.$store.state[storeKey]);
            }

            return lenders
                .map(lender => {
                    return {
                        ...lender,
                        model: new this.$models[modelKey](lender)
                    };
                        
                })
                .sort((a, b) => (b.model._core.source_sales_stats?.total?.clicks ?? 0) - (a.model._core.source_sales_stats?.total?.clicks ?? 0))
                .slice(0, 4);
        },
        getDataToShow(lenders, loanType) {
            return lenders.map(lender => ({
                name: lender.data.title,
                popularityTextHtml: this.getPopularityTextHtml(lender.model, loanType),
                link: this.$prismic.linkResolver(lender),
                logoData: this.getLogoData(lender),
                highlightedItems: this.getHighlightedData(
                    lender,
                    loanType
                )
            }));
        },
        getPopularityTextHtml(model, loanType) {
            const translation = loanType === 'corporateLoan' 
                ? this.$translate('featured_popular_loans_company_loan_picked_by_users', 'Vald av {count} företag denna månad')
                : this.$translate('featured_popular_loans_loan_picked_by_users', 'Vald av {count} personer denna månad');

            const lenderClicksPerMonth = model._core.source_sales_stats?.total?.clicks ?? 0;
            const htmlText = translation
                .replace('{count}', `<span class="p-0 fw-bold">${lenderClicksPerMonth}</span>`);
            
            return lenderClicksPerMonth > 0
                ? htmlText
                : null;
        },
        getLogoData(lender) {
            const isLenderIcon = lender.data.icon?.url;
            return {
                logo: isLenderIcon 
                    ? lender.data.icon 
                    : lender.data.image,
                width: isLenderIcon 
                    ? '40' 
                    : '60',
                height: isLenderIcon 
                    ? '40' 
                    : 'auto'
            };
        },
        getHighlightedData(lender, loanType) {
            if (loanType === 'corporateLoan') {
                return [
                    {
                        label: this.$translate('loan_amount', 'Lånebelopp'),
                        value: lender.model.amountString
                    },
                    {
                        label: this.$translate('duration', 'Löptid'),
                        value: lender.model.getDurationString(this.$translate('month_short', 'mån'), this.$translate('year', 'år'))
                    },
                    {
                        label: this.$translate('requires_collateral_in_company', 'Säkerhet i företaget'),
                        value: lender.model.requiresCollateral 
                            ? this.$translate('required', 'Krävs') 
                            : this.$translate('not_required', 'Krävs ej') 
                    }
                ];
            }

            if (loanType === 'mortgageLoan') {
                const fallbackIcon = '<i class="fa-solid fa-question yellow"></i>';
                return [
                    {
                        label: `${this.$translate('lm_list_interest_header', 'Listränta')} - ${this.$translate('lm_3m_label', '3 månader (rörlig)')}`,
                        value: lender.model.listInterest3m
                            ? lender.model.listInterestStrings['3m']
                            : fallbackIcon
                    },
                    {
                        label: `${this.$translate('lm_average_interest_header', 'Snittränta')} - ${this.$translate('lm_3m_label', '3 månader (rörlig)')}`,
                        value: lender.model.averageInterest3m
                            ? lender.model.averageInterestStrings['3m'] 
                            : fallbackIcon
                    }
                ];
            }
            
            return [
                {
                    label: this.$translate('amount', 'Belopp'),
                    value: lender.model.amountString
                },
                {
                    label: this.$translate('effective_interest', 'Effektiv ränta'),
                    value: lender.model.effectiveInterestString
                }
            ];
        },
        getGeneralLink(linkKey, textKey) {
            const typeSpecificLink = this.data.primary[linkKey];
            const typeSpecificLinkText = this.data.primary[textKey];
            return {
                link: this.$validateContentRelationship(typeSpecificLink) 
                    ? typeSpecificLink
                    : null,
                text: typeSpecificLinkText
            };
        }
    }
};
</script>

<style lang="scss" scoped>
.featured-popular-loans::v-deep {
   
    .tabs-wrapper {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 1.5rem;

        @include device(pad) {
            flex-direction: row;
            justify-content: center;
            flex-wrap: wrap;
        }

        .category-tab {
            width: 100%;
            background: $white;
            @include border();
            padding: 0.75rem 1.5rem;
            @include border-radius(3px);
            font-weight: bold;
            cursor: pointer;
            transition: all 0.2s ease;
            color: $font;

            &:hover {
                background-color: $lightgray;
            }

            &.is-active {
                background-color: $green;
                color: $white;
            }

            @include device(pad) {
                width: unset;
            }
        }
    }

    .text {
        margin-bottom: 2rem;
        * {
            text-align: center;
        }
    }

    .text-center {
        text-align: center;
    }
    
    .mt-4 {
        margin-top: 1.5rem;
    }

    .fw-bold {
        font-weight: bold;
    }
    .p-0 {
        padding: 0 !important;
    }
    .popularity-text {
        @include font-size(14px, 8px);

        span {
           font-weight: bold;
        }
    }
}
</style>